import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import createEmailCampaign from '../../../api/graphql/mutations/createEmailCampaign';
import cloneEmailCampaign from '../../../api/graphql/mutations/cloneEmailCampaign';
import { keyProblem } from '../../../utils/emailCampaignKey';
import Notification, { useNotification } from '../../Notification';

const KINDS = [
  { value: 'campaign', label: 'Campaign', blurb: 'Runs for weeks and has its own email. A presale.' },
  { value: 'list', label: 'List', blurb: 'Permanent, and uses the generic newsletter email. A city list.' },
  { value: 'system', label: 'System', blurb: 'Permanent, and fired by code rather than by a signup form.' },
];

// "New campaign" for the Emails list. Two ways to make one, behind one button:
//
//   Empty  - writes the campaign record and nothing else. This is the only option
//            that makes sense for a `list` scope, which by design has no bespoke
//            email and falls through to the generic newsletter forever.
//   Clone  - delegates to cloneEmailCampaign, unchanged. Cloning last year's
//            presale is how a campaign actually gets made.
//
// The approved design puts this on /cms/emails/new. It is a modal instead because
// that path is three segments and `/cms/:tab/:postCategory` already matches it as
// `exact`, so <Switch> would render the Emails tab and silently ignore the screen -
// the same trap documented at src/shared/routes.js:63. Same fields either way.
const EmailCampaignCreateModal = ({ campaigns, onCreated, onClose }) => {
  const [key, setKey] = useState('');
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('campaign');
  const [cloneFrom, setCloneFrom] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [status, handleStatus] = useNotification();

  // Only campaigns with plates can be cloned - there is nothing to copy otherwise,
  // and offering them would produce a "clone" that is just an empty campaign under
  // a name that implies its plates came across.
  const clonable = useMemo(
    () => campaigns.filter(campaign => campaign.hasAnyTemplate)
      .slice()
      .sort((a, b) => a.key.localeCompare(b.key)),
    [campaigns],
  );

  const trimmedKey = key.trim();
  const trimmedTitle = title.trim();
  const problem = key ? keyProblem(key) : null;
  const canSubmit = !isSaving && !keyProblem(key) && Boolean(trimmedTitle);

  const handleCreate = async () => {
    if (!canSubmit) return;

    setIsSaving(true);
    const result = cloneFrom
      ? await cloneEmailCampaign({ sourceKey: cloneFrom, newKey: trimmedKey, newTitle: trimmedTitle })
      : await createEmailCampaign({ key: trimmedKey, title: trimmedTitle, kind });
    setIsSaving(false);

    // Every failure mode gets a sentence. Silence here is the worst outcome: the
    // user retypes the same key, hits the same duplicate refusal, and blames the tool.
    if (!result) {
      handleStatus('icon-close-solid', 'Could not reach the server — check your connection and try again.', 'red');
      return;
    }
    if (!result.isAuth) {
      handleStatus('icon-close-solid', 'You are not authorized to create campaigns.', 'red');
      return;
    }
    if (!result.success || !result.campaign) {
      handleStatus('icon-close-solid', result.error || 'Could not create the campaign.', 'red');
      return;
    }

    onCreated(result.campaign);
  };

  return (
    <div className="emails-app emails-modal-card">
      <div className="mstack">
        {status.text && <Notification {...status} />}

        <div className="field">
          <label htmlFor="email-new-title">
            Name
            <input
              id="email-new-title"
              type="text"
              className="textinput"
              value={title}
              onChange={event => setTitle(event.target.value)}
              placeholder="Barcelona 2027 · general presale"
              autoComplete="off"
            />
          </label>
          <p className="hint">Shown in this dashboard only. Subscribers never see it.</p>
        </div>

        <div className="field">
          <label htmlFor="email-new-key">
            Scope word
            <input
              id="email-new-key"
              type="text"
              className="textinput mono"
              value={key}
              onChange={event => setKey(event.target.value)}
              placeholder="scope_presale27"
              autoComplete="off"
            />
          </label>
          <p className="hint">
            This one word does both jobs: it is the flag set on the Braze profile, and it is
            what finds the email. Type it into the newsletter widget to start the campaign.
          </p>
          <p className="hint">
            Creating it here only adds it to this dashboard — it does not create the scope in
            Braze, and on its own it sends nothing to anybody.
          </p>
          {problem && <p className="hint keyerror">{problem}</p>}
        </div>

        <div className="field">
          <label htmlFor="email-new-kind">
            Kind
            <select
              id="email-new-kind"
              className="textinput"
              value={kind}
              onChange={event => setKind(event.target.value)}
              disabled={Boolean(cloneFrom)}
            >
              {KINDS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <p className="hint">
            {cloneFrom
              ? 'A clone keeps the kind of the campaign it was copied from.'
              : (KINDS.find(option => option.value === kind) || {}).blurb}
          </p>
        </div>

        <div className="field">
          <label htmlFor="email-new-clone">
            Start from
            <select
              id="email-new-clone"
              className="textinput"
              value={cloneFrom}
              onChange={event => setCloneFrom(event.target.value)}
            >
              <option value="">Nothing — register the scope only</option>
              {clonable.map(campaign => (
                <option key={campaign.key} value={campaign.key}>
                  {`A copy of ${campaign.key} · ${campaign.templateCount} plate${campaign.templateCount === 1 ? '' : 's'}`}
                </option>
              ))}
            </select>
          </label>
          <p className="hint">
            {cloneFrom
              ? 'Every plate is copied word for word and flagged needs review. That flag is a label, not a lock — the copy is live to any signup carrying the new scope word, so check dates and links first.'
              : 'An empty campaign has no email of its own. A list scope uses the generic newsletter, which is usually what you want; a campaign scope needs its first plate writing.'}
          </p>
        </div>

        <div className="mactions">
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn" onClick={handleCreate} disabled={!canSubmit}>
            {isSaving ? 'Creating…' : 'Create campaign'}
          </button>
        </div>
      </div>
    </div>
  );
};

EmailCampaignCreateModal.propTypes = {
  campaigns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    hasAnyTemplate: PropTypes.bool,
    templateCount: PropTypes.number,
  })),
  onCreated: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

EmailCampaignCreateModal.defaultProps = {
  campaigns: [],
};

export default EmailCampaignCreateModal;
